@if (isLoading()) {
  <lib-overlay-loader />
} @else {
  <div
    data-test="job-title-details-page"
    class="h-full"
    [class]="floatingPageClasses()">
    <!-- Floating: the whole page content is one card -->
    <div
      data-test="job-title-details-card"
      class="flex flex-col justify-between h-full"
      [class.floating-card]="floating()"
      [class.p-2xl]="floating()">
      <div>
        <ng-template #backButton>
          <a
            data-test="job-title-details-back"
            (click)="goBack()"
            class="bg-white">
            <lib-chevron-down-icon
              [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
          </a>
        </ng-template>

        @if (!floating()) {
          <div class="flex justify-between items-center pb-md mb-lg">
            <ng-container [ngTemplateOutlet]="backButton" />
          </div>
        }

        <div
          class="flex justify-between items-center border-b border-neutral-200 pb-lg mb-2xl">
          <div class="flex gap-lg items-center">
            @if (floating()) {
              <ng-container [ngTemplateOutlet]="backButton" />
            }
            <lib-avatar
              [size]="''"
              [fullName]="jobTitleDetails()?.name ?? ''"
              [imgDimensions]="{ width: 64, height: 64 }"
              [placeholderTextClass]="'text-primary-500 font-medium text-2xl'"
              borderRadius="rounded-full"></lib-avatar>

            <div class="flex flex-col gap-md">
              <div class="flex items-center gap-sm">
                <h2 class="font-semibold text-2xl text-neutral-900 text-2xl">
                  {{ jobTitleDetails()?.name }}
                </h2>
                @if (jobTitleDetails()?.identifier) {
                  <span class="text-neutral-300 font-semibold no-arabic-font"
                    >|</span
                  >
                  <span class="text-primary-500 text-xl font-medium text-2xl">
                    {{ jobTitleDetails()?.identifier }}
                  </span>
                }
              </div>

              <div class="flex items-center gap-sm">
                <p class="text-sm font-medium text-sm text-neutral-500">
                  #{{ jobTitleDetails()?.id }}
                </p>

                @if (jobTitleDetails()?.state) {
                  <lib-badge
                    [label]="jobTitleDetails()?.state?.label"
                    [badgeClass]="
                      'px-sm border-none text-xs font-medium text-white ' +
                      (jobTitleDetails()?.state?.value | userStatus)
                    " />
                }
              </div>
            </div>
          </div>

          <a
            [routerLink]="[
              `${ADMIN_ROUTES.JOB_TITLES.FORM}` | tenantPath: 'admin',
              jobTitleId,
            ]"
            id="job_title_details_screen_edit_button"
            class="bg-white text-primary-600 font-medium text-base py-sm px-2xl border-none rounded-md gap-sm flex">
            <lib-edit-icon [iconClass]="'size-5 text-primary-600'" />

            {{ 'job_title_details_screen_edit_button' | transloco }}
          </a>
        </div>

        <div
          class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
          <div>
            <p class="view-mode-label">
              {{ 'job_titles_form_track_label' | transloco }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                jobTitleDetails()?.track
                  ? jobTitleDetails()?.track?.label
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{ 'job_titles_form_radford_grade_label' | transloco }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                jobTitleDetails()?.radford_grade
                  ? jobTitleDetails()?.radford_grade?.label
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{ 'job_titles_form_job_family_label' | transloco }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                jobTitleDetails()?.job_family
                  ? jobTitleDetails()?.job_family?.label
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-b border-neutral-200 py-lg mb-lg">
          <div class="flex items-center gap-lg">
            <h6 class="text-neutral-900 font-semibold text-lg">
              {{ 'job_titles_details_assigned_users' | transloco }}
            </h6>

            <lib-badge
              [label]="totalUsersCount()"
              badgeClass="px-sm border-none text-sm font-medium bg-primary-50 text-primary-600" />
          </div>

          <div>
            <div class="search-input relative flex gap-md items-center">
              <label
                class="flex items-center w-full border border-neutral-300 rounded-md text-sm p-md">
                <input
                  type="text"
                  [id]="'search'"
                  [placeholder]="'search_by_name_placeholder' | transloco"
                  [style.cursor]="
                    !jobTitleUsers().length && !searchTerm()
                      ? 'not-allowed'
                      : ''
                  "
                  [disabled]="!jobTitleUsers().length && !searchTerm()"
                  class="bg-transparent text-base w-full pe-2xl placeholder:text-sm placeholder:text-neutral-500"
                  [ngModel]="searchTerm()"
                  (ngModelChange)="onSearchTermChange($event)" />
                <div
                  class="icon absolute end-md inset-y-md cursor-pointer flex items-center">
                  <lib-search-icon [iconClass]="'size-5 text-neutral-500'" />
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="pe-md overflow-auto custom-height">
          <lib-group-user-card
            [list]="jobTitleUsers()"
            itemType="User"
            [mode]="'view'"></lib-group-user-card>
        </div>
      </div>

      @if (jobTitleUsers().length) {
        <div>
          <lib-paginator
            class="mt-4xl"
            [sizeOptionsLabel]="
              'pagination_number_of_rows_per_page_label' | transloco
            "
            [nextBtnLabel]="'pagination_next_button_label' | transloco"
            [previousBtnLabel]="'pagination_previous_button_label' | transloco"
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [totalItems]="+totalUsersCount()"
            [pageItemCount]="jobTitleUsers().length"
            [showChangeSize]="true"
            (goTo)="handlePagination($event)"
            (next)="handlePagination($event)"
            (previous)="handlePagination($event)"
            [showChangeSize]="false"></lib-paginator>
        </div>
      }
    </div>
  </div>
}
